import { useCallback, type MutableRefObject } from "react";
import * as api from "../api/disciplio-api";
import { captureEvent } from "../analytics";
import { getTodayKey } from "../disciplio/logic";
import { appendCustomMetricUnit } from "../disciplio/metric-units";
import type {
  AppState,
  DailyItem,
  DisciplioSettings,
  CalendarHabitTimeScopeMode,
  HabitTimeScope,
  MonthlyGoal,
  RagSource,
  UserProfile,
  WeeklyGoal,
} from "../disciplio/types";
import {
  type Action,
  type DailyPatch,
  type DailyItemMove,
  type DailyUpdateOutcome,
  persistCurrentMonth,
  persistWeek,
} from "./disciplio-context-state";

type Dispatch = (action: Action) => void;

export function useDisciplioActions(
  state: AppState,
  dispatch: Dispatch,
  pendingDailyDatesRef?: MutableRefObject<Set<string>>,
  monthlyGoalMutationRevisionRef?: MutableRefObject<number>,
  onStreakChanged?: () => void,
  onPlanningProgressChanged?: () => Promise<void>,
  onDailyItemCreated?: () => void
) {
  const toggleItem = useCallback(
    (id: string, checked: boolean, dateKey?: string) => {
      const targetDate = dateKey ?? getTodayKey(state.settings);
      dispatch({
        type: "updateDailyItem",
        dateKey: targetDate,
        id,
        patch: { done: checked },
      });
      pendingDailyDatesRef?.current.add(targetDate);
      const itemType = (state.dailyItemsByDate[targetDate] ?? []).find(
        (entry) => entry.id === id
      )?.type;
      Promise.resolve()
        .then(() => {
          const item = (state.dailyItemsByDate[targetDate] ?? []).find(
            (entry) => entry.id === id
          );
          return item?.recurring
            ? api.completeHabitOccurrence(targetDate, id, checked)
            : api.completeDailyItem(id, checked);
        })
        .then(() =>
          Promise.all([
            api.getDailyPlan(targetDate),
            api.getCompletionHistory(730, state.settings),
          ])
        )
        .then(([items, history]) => {
          dispatch({ type: "replaceDailyItems", dateKey: targetDate, items });
          dispatch({ type: "replaceCompletionHistory", history });
          if (checked) {
            captureEvent("daily_task_completed", { item_type: itemType });
          }
          onStreakChanged?.();
          return onPlanningProgressChanged?.();
        })
        .catch(() => undefined)
        .finally(() => pendingDailyDatesRef?.current.delete(targetDate));
    },
    [
      dispatch,
      onPlanningProgressChanged,
      onStreakChanged,
      pendingDailyDatesRef,
      state.dailyItemsByDate,
      state.settings,
    ]
  );

  const addItem = useCallback(
    async (item: DailyItem, dateKey?: string, notifyCreation = true) => {
      const targetDate = dateKey ?? getTodayKey(state.settings);
      dispatch({ type: "addDailyItem", dateKey: targetDate, item });
      pendingDailyDatesRef?.current.add(targetDate);
      try {
        const saved = await api.saveDailyItem(targetDate, item);
        if (!saved?.dailyPlanItemId) {
          dispatch({
            type: "deleteDailyItem",
            dateKey: targetDate,
            id: item.id,
          });
          return false;
        }
        dispatch({
          type: "replaceDailyItemId",
          dateKey: targetDate,
          localId: item.id,
          remoteId: saved.dailyPlanItemId,
        });
        captureEvent("daily_task_created", { item_type: item.type });
        if (notifyCreation) onDailyItemCreated?.();
        try {
          const history =
            item.type !== "habit" || item.repeat.cadence === "none"
              ? await api.getCompletionHistory(730, state.settings)
              : await Promise.all(
                  Object.keys(state.dailyItemsByDate).map(
                    async (existingDateKey) => {
                      const items = await api.getDailyPlan(existingDateKey);
                      dispatch({
                        type: "replaceDailyItems",
                        dateKey: existingDateKey,
                        items,
                      });
                    }
                  )
                ).then(() => api.getCompletionHistory(730, state.settings));
          if (history) dispatch({ type: "replaceCompletionHistory", history });
        } catch {
          // The task is already safely persisted; a later refresh can restore auxiliary state.
        }
        onStreakChanged?.();
        try {
          await onPlanningProgressChanged?.();
        } catch {
          // The persisted task remains valid even if the background refresh fails.
        }
        return true;
      } catch {
        dispatch({ type: "deleteDailyItem", dateKey: targetDate, id: item.id });
        return false;
      } finally {
        pendingDailyDatesRef?.current.delete(targetDate);
      }
    },
    [
      dispatch,
      onDailyItemCreated,
      onPlanningProgressChanged,
      onStreakChanged,
      pendingDailyDatesRef,
      state.dailyItemsByDate,
      state.settings,
    ]
  );

  const updateItem = useCallback(
    (
      id: string,
      patch: DailyPatch,
      dateKey?: string,
      habitTimeScope?: HabitTimeScope,
      calendarHabitTimeScopeMode?: CalendarHabitTimeScopeMode
    ): Promise<DailyUpdateOutcome> => {
      const targetDate = dateKey ?? getTodayKey(state.settings);
      const current = (state.dailyItemsByDate[targetDate] ?? []).find(
        (item) => item.id === id
      );
      if (!current) return Promise.resolve("failed");
      dispatch({ type: "updateDailyItem", dateKey: targetDate, id, patch });
      const { done: requestedDone, ...nonCompletionPatch } = patch;
      const isRecurringHabit = Boolean(current.recurring);
      const completionChanged =
        requestedDone !== undefined && requestedDone !== current.done;
      const hasNonCompletionChanges = Object.entries(nonCompletionPatch).some(
        ([key, value]) => current[key as keyof DailyItem] !== value
      );
      pendingDailyDatesRef?.current.add(targetDate);
      return Promise.resolve()
        .then(async () => {
          if (!isRecurringHabit || hasNonCompletionChanges) {
            await api.updateDailyItem(
              targetDate,
              { ...current, ...nonCompletionPatch },
              habitTimeScope,
              isRecurringHabit,
              calendarHabitTimeScopeMode
            );
          }
          if (completionChanged) {
            if (isRecurringHabit) {
              await api.completeHabitOccurrence(targetDate, id, requestedDone);
            } else {
              await api.completeDailyItem(id, requestedDone);
            }
          }
        })
        .then(async () => {
          const datesToRefresh =
            habitTimeScope === "FUTURE"
              ? Object.keys(state.dailyItemsByDate).filter(
                  (key) => key >= targetDate
                )
              : [targetDate];
          const refreshed = await Promise.all(
            datesToRefresh.map(
              async (key) => [key, await api.getDailyPlan(key)] as const
            )
          );
          refreshed.forEach(([key, items]) =>
            dispatch({ type: "replaceDailyItems", dateKey: key, items })
          );
          return api.getCompletionHistory(730, state.settings);
        })
        .then((history) =>
          dispatch({ type: "replaceCompletionHistory", history })
        )
        .then(() => onStreakChanged?.())
        .then(async () => {
          if (calendarHabitTimeScopeMode === "remember" && habitTimeScope) {
            dispatch({
              type: "saveSettings",
              settings: {
                ...state.settings,
                calendarHabitTimeScope: habitTimeScope,
                calendarHabitTimeScopeExpiresAt: new Date(
                  Date.now() + 60 * 60 * 1000
                ).toISOString(),
              },
            });
          }
          await onPlanningProgressChanged?.();
          return "saved" as const;
        })
        .catch(async (error): Promise<DailyUpdateOutcome> => {
          dispatch({
            type: "updateDailyItem",
            dateKey: targetDate,
            id,
            patch: current,
          });
          try {
            const items = await api.getDailyPlan(targetDate);
            dispatch({ type: "replaceDailyItems", dateKey: targetDate, items });
          } catch {
            // The original item is already restored locally; a later refresh can reconcile it.
          }
          return typeof api.isCalendarHabitTimeScopeRequiredError ===
            "function" && api.isCalendarHabitTimeScopeRequiredError(error)
            ? "calendar-habit-time-scope-required"
            : "failed";
        })
        .finally(() => pendingDailyDatesRef?.current.delete(targetDate));
    },
    [
      dispatch,
      onPlanningProgressChanged,
      onStreakChanged,
      pendingDailyDatesRef,
      state.dailyItemsByDate,
      state.settings,
    ]
  );

  const deleteItem = useCallback(
    (id: string, dateKey?: string, habitScope?: "ONE" | "ALL") => {
      const targetDate = dateKey ?? getTodayKey(state.settings);
      dispatch({ type: "deleteDailyItem", dateKey: targetDate, id });
      pendingDailyDatesRef?.current.add(targetDate);
      api
        .deleteDailyItem(targetDate, id, habitScope)
        .then(() =>
          Promise.all([
            api.getDailyPlan(targetDate),
            api.getCompletionHistory(730, state.settings),
          ])
        )
        .then(([items, history]) => {
          dispatch({ type: "replaceDailyItems", dateKey: targetDate, items });
          dispatch({ type: "replaceCompletionHistory", history });
          onStreakChanged?.();
          return onPlanningProgressChanged?.();
        })
        .catch(() => undefined)
        .finally(() => pendingDailyDatesRef?.current.delete(targetDate));
    },
    [
      dispatch,
      onPlanningProgressChanged,
      onStreakChanged,
      pendingDailyDatesRef,
      state.settings,
    ]
  );

  const restoreItem = useCallback(
    (item: DailyItem, dateKey?: string) => {
      void addItem(item, dateKey, false);
    },
    [addItem]
  );

  const moveItem = useCallback(
    (
      fromDateKey: string,
      toDateKey: string,
      itemId: string,
      patch?: DailyPatch
    ) => {
      const moving = (state.dailyItemsByDate[fromDateKey] ?? []).find(
        (item) => item.id === itemId
      );
      if (!moving) return Promise.resolve(false);
      const next = { ...moving, ...(patch ?? {}) };
      dispatch({ type: "deleteDailyItem", dateKey: fromDateKey, id: itemId });
      dispatch({ type: "addDailyItem", dateKey: toDateKey, item: next });
      pendingDailyDatesRef?.current.add(fromDateKey);
      pendingDailyDatesRef?.current.add(toDateKey);
      return Promise.resolve()
        .then(async () => {
          if (!api.hasApiSession()) return true;
          const isRemote =
            api.hasApiSession() && /^[0-9a-f-]{36}$/i.test(itemId);
          if (isRemote) {
            const saved = await api.moveDailyItem(
              fromDateKey,
              itemId,
              toDateKey,
              next.time
            );
            if (!saved) throw new Error("Daily item move could not be saved");
          } else {
            const saved = await api.saveDailyItem(toDateKey, next);
            if (!saved) throw new Error("Daily item move could not be saved");
          }
          const [fromItems, toItems, history] = await Promise.all([
            api.getDailyPlan(fromDateKey).catch(() => []),
            api.getDailyPlan(toDateKey).catch(() => []),
            api
              .getCompletionHistory(730, state.settings)
              .catch(() => state.completionHistory),
          ]);
          dispatch({
            type: "replaceDailyItems",
            dateKey: fromDateKey,
            items: fromItems,
          });
          dispatch({
            type: "replaceDailyItems",
            dateKey: toDateKey,
            items: toItems,
          });
          dispatch({ type: "replaceCompletionHistory", history });
          onStreakChanged?.();
          await onPlanningProgressChanged?.();
          return true;
        })
        .catch(async () => {
          if (api.hasApiSession()) {
            const [fromItems, toItems] = await Promise.all([
              api.getDailyPlan(fromDateKey).catch(() => null),
              api.getDailyPlan(toDateKey).catch(() => null),
            ]);
            if (fromItems)
              dispatch({
                type: "replaceDailyItems",
                dateKey: fromDateKey,
                items: fromItems,
              });
            if (toItems)
              dispatch({
                type: "replaceDailyItems",
                dateKey: toDateKey,
                items: toItems,
              });
          }
          return false;
        })
        .finally(() => {
          pendingDailyDatesRef?.current.delete(fromDateKey);
          pendingDailyDatesRef?.current.delete(toDateKey);
        });
    },
    [
      dispatch,
      onPlanningProgressChanged,
      onStreakChanged,
      pendingDailyDatesRef,
      state.completionHistory,
      state.dailyItemsByDate,
      state.settings,
    ]
  );

  const moveDailyItems = useCallback(
    async (moves: DailyItemMove[]): Promise<boolean> => {
      const uniqueMoves = moves.filter(
        (move, index, all) =>
          all.findIndex(
            (candidate) =>
              candidate.itemId === move.itemId &&
              candidate.fromDateKey === move.fromDateKey
          ) === index
      );
      if (!uniqueMoves.length) return true;

      const affectedDates = new Set<string>();
      uniqueMoves.forEach((move) => {
        affectedDates.add(move.fromDateKey);
        affectedDates.add(move.toDateKey);
      });
      const refreshAffectedDates = async () => {
        if (!api.hasApiSession()) return;
        const refreshed = await Promise.all(
          [...affectedDates].map(async (dateKey) => {
            try {
              return [dateKey, await api.getDailyPlan(dateKey)] as const;
            } catch {
              return null;
            }
          })
        );
        refreshed.forEach((entry) => {
          if (entry) {
            dispatch({
              type: "replaceDailyItems",
              dateKey: entry[0],
              items: entry[1],
            });
          }
        });
      };

      const entries = uniqueMoves.map((move) => ({
        move,
        item: (state.dailyItemsByDate[move.fromDateKey] ?? []).find(
          (candidate) => candidate.id === move.itemId
        ),
      }));
      if (entries.some(({ item }) => !item)) {
        await refreshAffectedDates();
        return false;
      }

      entries.forEach(({ move, item }) => {
        if (move.fromDateKey === move.toDateKey) {
          dispatch({
            type: "updateDailyItem",
            dateKey: move.fromDateKey,
            id: move.itemId,
            patch: { time: move.time },
          });
        } else if (item) {
          dispatch({
            type: "moveDailyItem",
            fromDateKey: move.fromDateKey,
            toDateKey: move.toDateKey,
            itemId: move.itemId,
            patch: { time: move.time },
          });
        }
      });
      affectedDates.forEach((dateKey) =>
        pendingDailyDatesRef?.current.add(dateKey)
      );

      try {
        if (!api.hasApiSession()) return true;

        const results = await Promise.allSettled(
          entries.map(async ({ move, item }) => {
            if (!item) return false;
            if (move.fromDateKey === move.toDateKey) {
              const saved = await api.updateDailyItem(
                move.fromDateKey,
                { ...item, time: move.time },
                undefined,
                false
              );
              return Boolean(saved);
            }
            const saved = await api.moveDailyItem(
              move.fromDateKey,
              move.itemId,
              move.toDateKey,
              move.time
            );
            return Boolean(saved);
          })
        );
        await refreshAffectedDates();
        try {
          const history = await api.getCompletionHistory(730, state.settings);
          dispatch({ type: "replaceCompletionHistory", history });
        } catch {
          // Calendar movement is already persisted; a later refresh can restore history.
        }
        try {
          await onPlanningProgressChanged?.();
        } catch {
          // The persisted movement remains valid even if progress refresh fails.
        }
        return results.every(
          (result) => result.status === "fulfilled" && result.value
        );
      } catch {
        // refreshAffectedDates handles individual refresh failures and keeps the
        // optimistic state reconciled wherever the server is reachable.
        return false;
      } finally {
        affectedDates.forEach((dateKey) =>
          pendingDailyDatesRef?.current.delete(dateKey)
        );
      }
    },
    [
      dispatch,
      onPlanningProgressChanged,
      pendingDailyDatesRef,
      state.dailyItemsByDate,
      state.settings,
    ]
  );

  const duplicateItem = useCallback(
    async (
      itemId: string,
      sourceDateKey: string,
      targetDateKeys: string[],
      keepTime: boolean,
      keepWeeklyGoalConnected: boolean
    ) => {
      const targetDates = [...new Set(targetDateKeys)].filter(
        (dateKey) => dateKey !== sourceDateKey
      );
      if (!targetDates.length) return false;
      targetDates.forEach((dateKey) =>
        pendingDailyDatesRef?.current.add(dateKey)
      );
      try {
        const created = await api.duplicateDailyItem(
          sourceDateKey,
          itemId,
          targetDates,
          keepTime,
          keepWeeklyGoalConnected
        );
        if (!created) return false;
        // The POST is the authoritative mutation. Reconcile only dates already in
        // memory; unloaded dates will be fetched normally when the user visits them.
        const loadedTargetDates = targetDates.filter((dateKey) =>
          Object.prototype.hasOwnProperty.call(state.dailyItemsByDate, dateKey)
        );
        await Promise.all(
          loadedTargetDates.map(async (dateKey) => {
            try {
              const items = await api.getDailyPlan(dateKey);
              dispatch({ type: "replaceDailyItems", dateKey, items });
            } catch {
              // Cache reconciliation is best effort after the committed POST.
            }
          })
        );
        try {
          await onPlanningProgressChanged?.();
        } catch {
          // A committed duplication must not be reported as a failed mutation.
        }
        return true;
      } catch {
        return false;
      } finally {
        targetDates.forEach((dateKey) =>
          pendingDailyDatesRef?.current.delete(dateKey)
        );
      }
    },
    [
      dispatch,
      onPlanningProgressChanged,
      pendingDailyDatesRef,
      state.dailyItemsByDate,
    ]
  );

  const saveProfile = useCallback(
    async (profile: UserProfile) => {
      dispatch({ type: "saveProfile", profile });
      await api.updateProfile(profile).catch(() => undefined);
      const syncedGoals = await api.syncProfileGoals(profile).catch(() => []);
      // Emit a second state update after the remote goal sync. The onboarding
      // journey observes state updates, so it must refresh after GoalEntity
      // persistence has completed rather than racing the first optimistic update.
      dispatch({
        type: "saveProfile",
        profile: api.profileWithGoalsIndex(profile, syncedGoals),
      });
    },
    [dispatch]
  );

  const saveSettings = useCallback(
    (settings: DisciplioSettings) => {
      dispatch({ type: "saveSettings", settings });
      api
        .updateSettings(settings)
        .then(async () => {
          onStreakChanged?.();
          await onPlanningProgressChanged?.();
        })
        .catch(() => undefined);
    },
    [dispatch, onPlanningProgressChanged, onStreakChanged]
  );

  const rememberMetricUnit = useCallback(
    (unit: string) => {
      const nextSettings = appendCustomMetricUnit(state.settings, unit);
      if (
        nextSettings.customMetricUnits.join("\n") ===
        state.settings.customMetricUnits.join("\n")
      )
        return;
      dispatch({ type: "saveSettings", settings: nextSettings });
      api
        .updateSettings(nextSettings)
        .then(async () => {
          onStreakChanged?.();
          await onPlanningProgressChanged?.();
        })
        .catch(() => undefined);
    },
    [dispatch, onPlanningProgressChanged, onStreakChanged, state.settings]
  );

  const setFeedback = useCallback(
    (text: string) => dispatch({ type: "feedback", text }),
    [dispatch]
  );
  const dismissSuggestion = useCallback(
    (id: string) => dispatch({ type: "dismissSuggestion", id }),
    [dispatch]
  );

  const addMonthlyGoal = useCallback(
    (goal: MonthlyGoal) => {
      const nextGoals = [...state.monthlyGoals, goal];
      dispatch({ type: "addMonthlyGoal", goal });
      persistCurrentMonth(nextGoals, dispatch);
    },
    [dispatch, state.monthlyGoals]
  );

  const updateMonthlyGoal = useCallback(
    (
      id: string,
      patch: Partial<
        Pick<
          MonthlyGoal,
          | "title"
          | "target"
          | "unit"
          | "progress"
          | "yearGoal"
          | "yearGoalId"
          | "completed"
        >
      >
    ) => {
      const nextGoals = state.monthlyGoals.map((goal) =>
        goal.id === id ? { ...goal, ...patch } : goal
      );
      dispatch({ type: "updateMonthlyGoal", id, patch });
      persistCurrentMonth(nextGoals, dispatch);
    },
    [dispatch, state.monthlyGoals]
  );

  const completeMonthlyGoal = useCallback(
    async (id: string, completed: boolean): Promise<boolean> => {
      const current = state.monthlyGoals.find((goal) => goal.id === id);
      if (!current) return false;
      if (monthlyGoalMutationRevisionRef) {
        monthlyGoalMutationRevisionRef.current += 1;
      }
      dispatch({ type: "updateMonthlyGoal", id, patch: { completed } });
      try {
        const saved = await api.completeMonthlyGoal(id, completed);
        if (!saved)
          throw new Error("Monthly goal completion could not be saved");
        dispatch({
          type: "updateMonthlyGoal",
          id,
          patch: { ...api.toMonthlyGoal(saved) },
        });
        void onPlanningProgressChanged?.();
        return true;
      } catch {
        dispatch({
          type: "updateMonthlyGoal",
          id,
          patch: { completed: current.completed },
        });
        return false;
      }
    },
    [
      dispatch,
      monthlyGoalMutationRevisionRef,
      onPlanningProgressChanged,
      state.monthlyGoals,
    ]
  );

  const deleteMonthlyGoal = useCallback(
    (id: string) => {
      const nextGoals = state.monthlyGoals.filter((goal) => goal.id !== id);
      dispatch({ type: "deleteMonthlyGoal", id });
      persistCurrentMonth(nextGoals, dispatch);
    },
    [dispatch, state.monthlyGoals]
  );

  const replaceMonthlyGoals = useCallback(
    (goals: MonthlyGoal[]) => {
      dispatch({ type: "replaceMonthlyGoals", goals });
    },
    [dispatch]
  );

  const addWeeklyGoal = useCallback(
    (weekStartDate: string, goal: WeeklyGoal) => {
      const nextGoal = { ...goal, weekStartDate };
      const nextGoals = [...state.weeklyGoals, nextGoal];
      dispatch({ type: "addWeeklyGoal", goal: nextGoal });
      persistWeek(
        weekStartDate,
        nextGoals,
        dispatch,
        onPlanningProgressChanged
      );
    },
    [dispatch, onPlanningProgressChanged, state.weeklyGoals]
  );

  const updateWeeklyGoal = useCallback(
    (
      id: string,
      patch: Partial<
        Pick<
          WeeklyGoal,
          "title" | "target" | "unit" | "progress" | "category" | "completed"
        >
      >
    ) => {
      const current = state.weeklyGoals.find((goal) => goal.id === id);
      const nextGoals = state.weeklyGoals.map((goal) =>
        goal.id === id ? { ...goal, ...patch } : goal
      );
      dispatch({ type: "updateWeeklyGoal", id, patch });
      persistWeek(
        current?.weekStartDate ?? api.currentWeekStartKey(),
        nextGoals,
        dispatch,
        onPlanningProgressChanged
      );
    },
    [dispatch, onPlanningProgressChanged, state.weeklyGoals]
  );

  const deleteWeeklyGoal = useCallback(
    (id: string) => {
      const current = state.weeklyGoals.find((goal) => goal.id === id);
      const nextGoals = state.weeklyGoals.filter((goal) => goal.id !== id);
      dispatch({ type: "deleteWeeklyGoal", id });
      persistWeek(
        current?.weekStartDate ?? api.currentWeekStartKey(),
        nextGoals,
        dispatch,
        onPlanningProgressChanged
      );
    },
    [dispatch, onPlanningProgressChanged, state.weeklyGoals]
  );

  const replaceWeeklyGoals = useCallback(
    (goals: WeeklyGoal[]) => {
      dispatch({ type: "replaceWeeklyGoals", goals });
    },
    [dispatch]
  );

  const addRagSource = useCallback(
    (source: RagSource) => dispatch({ type: "addRagSource", source }),
    [dispatch]
  );
  const replaceRagSources = useCallback(
    (sources: RagSource[]) => dispatch({ type: "replaceRagSources", sources }),
    [dispatch]
  );
  const updateRagSource = useCallback(
    (id: string, patch: Partial<RagSource>) =>
      dispatch({ type: "updateRagSource", id, patch }),
    [dispatch]
  );
  const deleteRagSource = useCallback(
    (id: string) => dispatch({ type: "deleteRagSource", id }),
    [dispatch]
  );

  return {
    toggleItem,
    addItem,
    updateItem,
    deleteItem,
    restoreItem,
    moveItem,
    moveDailyItems,
    duplicateItem,
    saveProfile,
    saveSettings,
    rememberMetricUnit,
    setFeedback,
    dismissSuggestion,
    addMonthlyGoal,
    updateMonthlyGoal,
    completeMonthlyGoal,
    deleteMonthlyGoal,
    replaceMonthlyGoals,
    addWeeklyGoal,
    updateWeeklyGoal,
    deleteWeeklyGoal,
    replaceWeeklyGoals,
    addRagSource,
    replaceRagSources,
    updateRagSource,
    deleteRagSource,
  };
}
